import { useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from "lodash";
import { Popover } from "react-tiny-popover";
import clsx from "clsx";
import styles from "./Conversation.module.scss";
import locale from "../../../constants/locale";
import { useTranslation } from "react-i18next";
import MessageFormatter from "./MessageFormatter";
import { useStore } from "../../../stores";
import { observer } from "mobx-react-lite";

interface SearchResult {
  id: number;
  text: string;
}

const ConversationSearch = ({ isMobile }: { isMobile?: boolean }) => {
  const { t } = useTranslation();
  const { messages, loadMessages } = useStore("chats");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [allMessageLoaded, setAllMessageLoaded] = useState(false);

  const handleCloseSearch = () => {
    setShowSearch(false);
    clearSearch();
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setHasSearched(false);
  };

  // Clear search results when popover is closed
  useEffect(() => {
    if (!showSearch) {
      clearSearch();
    }
  }, [showSearch]);

  const scrollToMessage = (messageId: number) => {
    const messageElement = document.querySelector(`#chat-message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth" });

      // Highlight the message temporarily
      messageElement.classList.add(styles.highlightedMessage);
      setTimeout(() => {
        messageElement.classList.remove(styles.highlightedMessage);
      }, 2000);
    }
  };

  // Search function that takes the query as a parameter
  const searchMessages = useCallback(
    async (query: string) => {
      console.log("searching", query, messages.length);
      if (!query.trim() || !messages.length) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }

      setIsSearching(true);
      setHasSearched(true);

      if (!allMessageLoaded && messages.length >= 200) {
        await loadMessages(1000);
        setAllMessageLoaded(true);
      }

      const lowerQuery = query.toLowerCase();
      const results: SearchResult[] = [];

      messages.forEach((message) => {
        if (results.length >= 20) {
          return;
        }
        // Search in text messages
        if (message.text?.toLowerCase().includes(lowerQuery)) {
          results.push({
            id: message.id,
            text: message.text,
          });
        }
      });

      setSearchResults(results);
      setIsSearching(false);
    },
    [messages, allMessageLoaded, loadMessages],
  );

  // Create a debounced version of the search function
  const debouncedSearch = useMemo(
    () => debounce((query: string) => searchMessages(query), 300),
    [searchMessages],
  );

  // Trigger search on query change
  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery); // Pass the current searchQuery value
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const handleSelectResult = (messageId: number) => {
    scrollToMessage(messageId);
    handleCloseSearch();
  };

  const searchContent = (
    <div className={styles.searchContainer}>
      <div className="field">
        <div className="control is-flex">
          <input
            className="input"
            type="search"
            placeholder={t(locale.searchInConversation)}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                searchMessages(searchQuery);
              }
            }}
            autoFocus
          />
        </div>
      </div>
      {hasSearched && (
        <div className={styles.searchResults}>
          {isSearching ? (
            <p className="has-text-centered py-2">Searching...</p>
          ) : searchResults.length > 0 ? (
            <>
              <p className="subtitle is-6 has-text-weight-semibold mb-2">
                {t(locale.foundMessages, { count: searchResults.length })}
              </p>
              <div className={styles.searchResultsList}>
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className={styles.searchResultItem}
                    onClick={() => handleSelectResult(result.id)}
                  >
                    <div className="line-clamp-2">
                      <MessageFormatter text={result.text ?? ""} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="has-text-centered py-2 has-text-grey">
              {t(locale.noMessagesFound)}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      isOpen={showSearch}
      padding={10}
      positions={["bottom", "left"]}
      onClickOutside={handleCloseSearch}
      containerStyle={{ zIndex: "100000000" }}
      containerClassName={styles.searchPopover}
      content={searchContent}
    >
      <button
        className={clsx("button", {
          "is-ghost no-focus": isMobile,
          "is-rounded has-text-primary": !isMobile,
        })}
        onClick={() => setShowSearch(!showSearch)}
      >
        <span style={{ position: "absolute" }} className="icon">
          <img src="/assets/search.svg" alt="Search" width={18} />
        </span>
      </button>
    </Popover>
  );
};

export default observer(ConversationSearch);
